/**
 * Genre name normalization and mapping
 *
 * Provides consistent genre naming between TMDB (Titlecase) and internal representation
 * Ensures database consistency by normalizing all genre names to Titlecase format
 */

// Standard genre names matching TMDB format (Titlecase)
const GENRE_NAME_MAP: Record<string, string> = {
  // Map lowercase/alternate formats to standard Titlecase
  action: "Action",
  adventure: "Adventure",
  animation: "Animation",
  comedy: "Comedy",
  crime: "Crime",
  documentary: "Documentary",
  drama: "Drama",
  family: "Family",
  fantasy: "Fantasy",
  history: "History",
  horror: "Horror",
  music: "Music",
  mystery: "Mystery",
  romance: "Romance",
  "science fiction": "Science Fiction",
  "science-fiction": "Science Fiction",
  "sci-fi": "Science Fiction",
  thriller: "Thriller",
  war: "War",
  western: "Western",

  // Already normalized (map to themselves for completeness)
  Action: "Action",
  Adventure: "Adventure",
  Animation: "Animation",
  Comedy: "Comedy",
  Crime: "Crime",
  Documentary: "Documentary",
  Drama: "Drama",
  Family: "Family",
  Fantasy: "Fantasy",
  History: "History",
  Horror: "Horror",
  Music: "Music",
  Mystery: "Mystery",
  Romance: "Romance",
  "Science Fiction": "Science Fiction",
  Thriller: "Thriller",
  War: "War",
  Western: "Western",
};

/**
 * Normalize a genre name to standard Titlecase format
 * Handles variations like lowercase, hyphenated, etc.
 */
export function normalizeGenreName(genre: string): string {
  const trimmed = genre.trim();
  const normalized = GENRE_NAME_MAP[trimmed] || GENRE_NAME_MAP[trimmed.toLowerCase()];

  if (normalized) {
    return normalized;
  }

  // Fallback: capitalize first letter of each word
  return trimmed
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

/**
 * Standard genre list for UI selectors
 * Matches TMDB genre naming convention (Titlecase)
 */
export const GENRE_OPTIONS = [
  { value: "Action", label: "Action" },
  { value: "Adventure", label: "Adventure" },
  { value: "Animation", label: "Animation" },
  { value: "Comedy", label: "Comedy" },
  { value: "Crime", label: "Crime" },
  { value: "Documentary", label: "Documentary" },
  { value: "Drama", label: "Drama" },
  { value: "Family", label: "Family" },
  { value: "Fantasy", label: "Fantasy" },
  { value: "History", label: "History" },
  { value: "Horror", label: "Horror" },
  { value: "Music", label: "Music" },
  { value: "Mystery", label: "Mystery" },
  { value: "Romance", label: "Romance" },
  { value: "Science Fiction", label: "Science Fiction" },
  { value: "Thriller", label: "Thriller" },
  { value: "War", label: "War" },
  { value: "Western", label: "Western" },
] as const;

/**
 * Normalize an array of genre names
 * Removes duplicates after normalization
 */
export function normalizeGenreArray(genres: string[]): string[] {
  const normalized = genres.map(normalizeGenreName);
  return Array.from(new Set(normalized)); // Remove duplicates
}
